import { useEffect, useState } from 'react';
import { DetectedFileKind } from '@signalvote/shared';
import { isDetectedFileKindVisibleForSubsidiary } from '@/lib/finance/processorSubsidiaryVisibility';

interface KindPickerProps {
  /**
   * Optional kind to omit from the picker. Set after the user clicked
   * "No, this is something else" on the confirmation banner — the picker
   * re-opens without the previously-detected option, per the PPR-003
   * acceptance criteria.
   */
  omitKind?: DetectedFileKind | null;
  /**
   * Pre-selected kind when the picker opens. The detector returns a dumb
   * header-token `guessedKind` for every unrecognized file; the panel passes it
   * here so the picker pre-selects a real default instead of a dead-end. The
   * preview shows the data before anything commits, so the guess only needs to
   * be reasonable — the treasurer can still pick anything. When omitted, the
   * picker falls back to the "Not sure / it's a mix" catch-all, which brings the
   * data in and sorts each row downstream (never a 0-row dead-end).
   */
  initialSelected?: DetectedFileKind;
  /**
   * Called when the user picks a built-in kind (Bank statement, Numero,
   * WinRed, etc.).
   */
  onPick: (kind: DetectedFileKind) => void;
  /**
   * Called when the user picks "Other payment processor" and types a name.
   * The custom name will train a new fingerprint under
   * <see cref="DetectedFileKind.Unattributed"/> in v1 — the processor
   * doesn't have an adapter yet, so the bytes land in the inbox preserved.
   */
  onPickOther?: (customName: string) => void;
  /**
   * Called for the "Not sure / it's a mix" escape hatch instead of
   * <code>onPick(BankCsv)</code>. Same bank/list import route, but it carries the
   * "the user says it's a mix" intent so the downstream bank UI can drop the
   * "this looks like a bank statement" framing and not assume a bank account.
   * When omitted, the mix option falls back to a plain <code>onPick(BankCsv)</code>.
   */
  onPickMixed?: () => void;
  onCancel: () => void;
  busy?: boolean;
}

interface PickerOption {
  /**
   * The radio's selection token. A real `DetectedFileKind` for the kind options,
   * or a synthetic token (`'Other'`, `'NotSureMix'`) the {@link handleImport}
   * tail translates into the kind it actually routes to.
   */
  selection: Selection;
  label: string;
}

/**
 * Kind picker the inbox shows when the file-type detector misses (no candidate,
 * multiple candidates, or the user clicked "this is something else" on a
 * high-confidence match). Pre-selects the detector's header-token `guessedKind`
 * (passed via `initialSelected`) instead of a dead-end — one confirm reaches the
 * preview + column mapping. The preview shows the data before anything commits.
 *
 * The picker drives <see cref="DetectorTrainRequestDto" /> on Import.
 */
// `'NotSureMix'` is the catch-all escape hatch: a file the treasurer can't
// cleanly call donations or expenses (a compiled mixed cash log) imports as
// bank/list rows and defers per-row meaning to the SortInbox triage. It routes
// to `BankCsv` — never the retired 0-row Unattributed dead-end.
type Selection = DetectedFileKind | 'Other' | 'NotSureMix';

export function KindPicker({ omitKind, initialSelected, onPick, onPickOther, onPickMixed, onCancel, busy }: KindPickerProps) {
  const [selected, setSelected] = useState<Selection>(
    initialSelected ?? 'NotSureMix',
  );
  const [otherName, setOtherName] = useState('');

  // Keep the shown selection in step with `initialSelected`. The parent keeps
  // this picker mounted while a re-detect can change the suggestion, so a
  // mount-once initializer would silently drift from the B2 nudge ("we've
  // pre-selected it"). Re-syncing on every change of `initialSelected`
  // guarantees the displayed radio can't contradict the nudge copy. A treasurer
  // pick within the same suggestion isn't clobbered: the effect only fires when
  // `initialSelected` itself changes, not on unrelated re-renders.
  //
  // This deliberately survives the "reset state on prop change" sweep that
  // deleted the equivalent Effects elsewhere. Those rebuilt an edit-in-progress
  // from an entity that a background refetch could change underneath the user;
  // this one fires only when the DETECTOR'S SUGGESTION changes, which is
  // precisely when the radio is supposed to move. A `key={initialSelected}`
  // remount would express the same rule, at the cost of also clearing a
  // half-typed "Other processor" name — a behaviour change with no bug behind
  // it, so the Effect stays.
  useEffect(() => {
    if (initialSelected !== undefined) {
      setSelected(initialSelected);
    }
  }, [initialSelected]);

  const options: PickerOption[] = [
    // One option covers a real bank statement AND a hand-compiled list of money
    // movements — both are a dated list of signed in/out rows the SortInbox
    // triage classifies per line. Treasurer-first, no engineer enum names.
    {
      selection: DetectedFileKind.BankCsv,
      label: 'Bank statement or a list of transactions — money in and out',
    },
    { selection: DetectedFileKind.NumeroContributions, label: 'Numero' },
    { selection: DetectedFileKind.WinRedContributions, label: 'WinRed' },
    { selection: DetectedFileKind.ActBlueContributions, label: 'ActBlue' },
    { selection: DetectedFileKind.AnedotContributions, label: 'Anedot' },
    { selection: DetectedFileKind.StripeCombined, label: 'Stripe' },
    // ADR-015 Tier 2 — off-platform hand-compiled sheets routed to the generic
    // content scanner. The off-platform caveat is inline (D9 cross-source
    // double-report mitigation): "Donations" here means money that did NOT come
    // through a processor, so a treasurer doesn't double-import a gift the
    // processor file already carries. Treasurer-first, no FEC framing.
    {
      selection: DetectedFileKind.Donations,
      label: 'Donations (off-platform — money that did NOT come through a processor)',
    },
    { selection: DetectedFileKind.Expenses, label: 'Expenses' },
    // The catch-all escape hatch. Replaces the retired 0-row "Not sure — figure
    // it out later" dead-end: instead of parking the bytes, it brings the data
    // in as bank/list rows and defers each row's meaning to the triage inbox.
    {
      selection: 'NotSureMix',
      label: "Not sure / it's a mix — bring it in and I'll sort each row",
    },
  ];

  const visibleOptions = options
    .filter((o) => (omitKind ? o.selection !== omitKind : true))
    // Hide the partisan-only upload options in the other party's build (WinRed is
    // GOP-only, ActBlue is Democrat-only); synthetic tokens ('Other'/'NotSureMix')
    // and every non-partisan kind stay visible.
    .filter((o) =>
      o.selection === DetectedFileKind.WinRedContributions ||
      o.selection === DetectedFileKind.ActBlueContributions
        ? isDetectedFileKindVisibleForSubsidiary(o.selection)
        : true,
    );

  const handleImport = () => {
    if (selected === 'Other') {
      const trimmed = otherName.trim();
      if (!trimmed) return; // disabled below; defensive guard.
      onPickOther?.(trimmed);
      return;
    }
    // The "Not sure / it's a mix" escape hatch routes into the bank/list mixed
    // path so the rows land as BankTransactions and triage sorts each — never
    // the retired 0-row Unattributed commit.
    if (selected === 'NotSureMix') {
      if (onPickMixed) {
        onPickMixed();
        return;
      }
      onPick(DetectedFileKind.BankCsv);
      return;
    }
    onPick(selected);
  };

  const importDisabled = busy || (selected === 'Other' && otherName.trim() === '');

  return (
    <div
      role="dialog"
      aria-label="Pick file kind"
      className="rounded-md border bg-card p-4 space-y-4"
    >
      <p className="text-sm font-medium">What kind of file is this?</p>
      <fieldset className="space-y-2">
        <legend className="sr-only">File kind</legend>
        {visibleOptions.map((option) => (
          <label
            key={option.selection}
            className="flex items-start gap-2 cursor-pointer text-sm"
          >
            <input
              type="radio"
              name="detector-kind"
              value={option.selection}
              checked={selected === option.selection}
              onChange={() => setSelected(option.selection)}
              disabled={busy}
              className="mt-1"
            />
            <span className="flex-1">{option.label}</span>
          </label>
        ))}
        {/* "Other payment processor → name: [______]" per design doc § picker.
            The custom name is forwarded to onPickOther so the parent can wire
            it through the discovery/onboarding flow (the bytes themselves
            land as Unattributed in v1 — no adapter for arbitrary names). */}
        <label className="flex items-start gap-2 cursor-pointer text-sm">
          <input
            type="radio"
            name="detector-kind"
            value="Other"
            checked={selected === 'Other'}
            onChange={() => setSelected('Other')}
            disabled={busy}
            className="mt-1"
          />
          <span className="flex-1 flex flex-wrap items-center gap-2">
            <span>Other payment processor →</span>
            <input
              type="text"
              aria-label="Other processor name"
              placeholder="name"
              value={otherName}
              onChange={(e) => {
                setOtherName(e.target.value);
                // Typing auto-selects the Other radio so the user doesn't
                // have to click both the radio AND the field.
                setSelected('Other');
              }}
              onFocus={() => setSelected('Other')}
              disabled={busy}
              className="rounded border px-2 py-0.5 text-sm w-40"
            />
          </span>
        </label>
      </fieldset>
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-60"
        >
          Cancel upload
        </button>
        <button
          type="button"
          onClick={handleImport}
          disabled={importDisabled}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {busy ? 'Importing…' : 'Import'}
        </button>
      </div>
    </div>
  );
}
